import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Camera, 
  Layers,
  TrendingUp,
  Clock,
  Bell,
  Calculator,
  LineChart,
  Award,
  Package,
  Scan,
  Shield,
  FileText,
  Upload,
  Send,
  Tag,
  Globe,
  Wrench,
  Box,
  LayoutDashboard,
  BarChart3,
  DollarSign,
  Wallet,
  Receipt,
  Truck,
  Users,
  Timer,
  Sparkles,
  Search,
  Store,
  Settings,
  UserCircle,
  Mail,
  MessageSquare,
  Heart,
  Gift,
  Tablet,
  BookOpen,
  UsersRound,
  Megaphone,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Bot
} from 'lucide-react';

const AllFeaturesSection = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const featureCategories = [
    {
      id: 'ai-pricing',
      title: 'AI-Powered Pricing & Estimation',
      icon: Zap,
      color: 'green',
      features: [
        { icon: DollarSign, name: 'Instant Trade-In Estimates', desc: 'Real-time buyback prices based on live eBay market data' },
        { icon: Camera, name: 'AI Scanner', desc: 'Point your camera at any device to identify model and get instant price' },
        { icon: Layers, name: 'Bulk Estimates', desc: 'Price multiple devices at once for large buyback deals' },
        { icon: TrendingUp, name: 'Price Prediction', desc: 'AI forecasts where prices are heading so you buy and sell at the right time' },
        { icon: Clock, name: 'Price History', desc: 'Track how any device\'s value has changed over time' },
        { icon: Bell, name: 'Price Alerts', desc: 'Get notified when a device hits your target price' },
        { icon: Calculator, name: 'Profit Calculator', desc: 'Know your exact margins before making a deal' },
        { icon: LineChart, name: 'Profit Forecasting', desc: 'Project future revenue based on current trends' },
        { icon: Award, name: 'Device Grading', desc: 'Standardized condition grading system for accurate pricing' },
      ]
    },
    {
      id: 'inventory',
      title: 'Inventory Management',
      icon: Package,
      color: 'blue',
      features: [
        { icon: Package, name: 'Full Inventory Tracking', desc: 'Track every device from purchase to sale' },
        { icon: Scan, name: 'Barcode Scanner', desc: 'Scan devices in and out with your camera' },
        { icon: Shield, name: 'IMEI Verification', desc: 'Check device blacklist status, carrier lock, and iCloud lock' },
        { icon: FileText, name: 'Device History', desc: 'Complete audit trail for every device in your system' },
        { icon: Upload, name: 'Bulk Import', desc: 'Import your existing inventory from spreadsheets' },
        { icon: Send, name: 'One-Click Listing', desc: 'List devices for sale on marketplaces instantly' },
        { icon: Tag, name: 'Shipping Labels', desc: 'Generate shipping labels directly from the app' },
        { icon: Globe, name: 'Multi-Currency Support', desc: 'Work with customers and suppliers in any currency' },
      ]
    },
    {
      id: 'repair',
      title: 'Repair Hub',
      icon: Wrench,
      color: 'orange',
      features: [
        { icon: Wrench, name: 'Repair Ticket Management', desc: 'Create, assign, and track repair jobs from start to finish' },
        { icon: Box, name: 'Repair Parts Inventory', desc: 'Track parts stock and costs' },
        { icon: LayoutDashboard, name: 'Repair Dashboard', desc: 'See all active repairs, turnaround times, and technician workload' },
        { icon: BarChart3, name: 'Repair Analytics', desc: 'Measure repair profitability, most common repairs, and efficiency' },
        { icon: Calculator, name: 'Repair Calculator', desc: 'Estimate repair costs and set profitable pricing' },
        { icon: DollarSign, name: 'Repair Cashflow', desc: 'Track money in and out of your repair operation' },
        { icon: Receipt, name: 'Repair Expenses', desc: 'Log all costs associated with repairs' },
        { icon: Shield, name: 'Repair Warranties', desc: 'Manage warranty claims and coverage periods' },
        { icon: Truck, name: 'Repair Suppliers', desc: 'Keep track of parts suppliers and pricing' },
        { icon: Users, name: 'Repair Team Management', desc: 'Assign jobs to technicians and monitor performance' },
        { icon: Bell, name: 'Repair Notifications', desc: 'Automatic status updates for customers' },
        { icon: Sparkles, name: 'Repair Quick Sale', desc: 'Fast checkout for completed repairs' },
        { icon: Timer, name: 'SLA Tracking', desc: 'Monitor service level agreements and turnaround targets' },
      ]
    },
    {
      id: 'deals',
      title: 'Deal Finder & Market Intelligence',
      icon: Search,
      color: 'purple',
      features: [
        { icon: Search, name: 'Deal Finder', desc: 'Scan eBay and Facebook Marketplace for profitable buying opportunities' },
        { icon: TrendingUp, name: 'Market Trends', desc: 'See which devices are trending up or down in value' },
        { icon: Search, name: 'Advanced Search', desc: 'Find exactly what you\'re looking for across marketplaces' },
        { icon: Store, name: 'Supplier Comparison', desc: 'Compare pricing across multiple suppliers' },
        { icon: Settings, name: 'Optimizer', desc: 'Get recommendations to maximize your profit margins' },
      ]
    },
    {
      id: 'crm',
      title: 'Customer Management (CRM)',
      icon: UserCircle,
      color: 'pink',
      features: [
        { icon: UserCircle, name: 'Customer Database', desc: 'Store customer info, transaction history, and preferences' },
        { icon: BarChart3, name: 'Customer Analytics', desc: 'Understand your best customers and buying patterns' },
        { icon: Mail, name: 'Customer Emails', desc: 'Send professional emails directly from the app' },
        { icon: MessageSquare, name: 'Follow-Up Surveys', desc: 'Automatically collect feedback after transactions' },
        { icon: Heart, name: 'Loyalty Program', desc: 'Reward repeat customers to keep them coming back' },
        { icon: MessageSquare, name: 'Messaging', desc: 'In-app messaging with customers' },
        { icon: FileText, name: 'Digital Intake Forms', desc: 'Paperless device intake for buyback and repair' },
        { icon: Globe, name: 'Self-Service Portal', desc: 'Let customers check repair status and submit devices online' },
        { icon: DollarSign, name: 'Cash Offers', desc: 'Generate professional cash offer sheets for walk-in customers' },
      ]
    },
    {
      id: 'analytics',
      title: 'Business Analytics & Finance',
      icon: BarChart3,
      color: 'cyan',
      features: [
        { icon: LayoutDashboard, name: 'Analytics Dashboard', desc: 'Revenue, profit, sell-through rates, and repair load at a glance' },
        { icon: LineChart, name: 'Revenue Split', desc: 'Track revenue by source (buyback vs repair)' },
        { icon: Wallet, name: 'Money Dashboard', desc: 'Full financial overview of your business' },
        { icon: Search, name: 'Phantom Profit Detection', desc: 'Identify hidden costs eating into your margins' },
        { icon: Award, name: 'Business Goals', desc: 'Set and track revenue and growth targets' },
        { icon: TrendingUp, name: 'Leaderboard', desc: 'Compare performance across team members or locations' },
      ]
    },
    {
      id: 'team',
      title: 'Team & Operations',
      icon: Users,
      color: 'indigo',
      features: [
        { icon: MessageSquare, name: 'Team Chat', desc: 'In-app communication with your team' },
        { icon: Tablet, name: 'Kiosk Mode', desc: 'Turn any iPad into a customer-facing trade-in station' },
        { icon: Receipt, name: 'Receipt Scanner', desc: 'Scan and digitize paper receipts' },
        { icon: Clock, name: 'Appointments', desc: 'Schedule buyback and repair appointments' },
        { icon: Gift, name: 'White Label Branding', desc: 'Customize the app with your own logo and colors' },
      ]
    },
    {
      id: 'ai',
      title: 'AI Assistant',
      icon: Bot,
      color: 'violet',
      features: [
        { icon: Bot, name: 'AI Chat', desc: 'Ask questions about pricing, inventory, or business strategy and get instant AI-powered answers' },
        { icon: Lightbulb, name: 'Smart Recommendations', desc: 'AI suggests what to buy, when to sell, and how to grow' },
      ]
    },
    {
      id: 'learning',
      title: 'Learning & Community',
      icon: BookOpen,
      color: 'teal',
      features: [
        { icon: BookOpen, name: 'Academy', desc: 'Tutorials and guides to master the resale and repair business' },
        { icon: UsersRound, name: 'Community', desc: 'Connect with other resellers and repair pros' },
        { icon: Megaphone, name: 'Marketing Hub', desc: 'Tools and templates to promote your business' },
        { icon: Lightbulb, name: 'Onboarding Tips', desc: 'Get started fast with guided walkthroughs' },
      ]
    },
  ];

  const colorClasses = {
    green: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20',
    blue: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20',
    orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    purple: 'bg-primary/10 text-primary border-primary/20',
    pink: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
    cyan: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    indigo: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
    violet: 'bg-violet-500/10 text-violet-500 border-violet-500/20',
    teal: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
  };

  return (
    <section id="all-features" className="py-16 sm:py-24 md:py-32 relative" data-testid="all-features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            Complete Feature List
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            Everything You Need in
            <span className="text-gradient"> One App</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-3xl mx-auto">
            2A Valuence replaces dozens of separate apps with a single powerful dashboard. 
            Buy, repair, price, and sell used electronics — all in one place.
          </p>
        </motion.div>

        {/* Feature Categories */}
        <div className="space-y-4">
          {featureCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.05 }}
              className="glass-card overflow-hidden"
            >
              {/* Category Header */}
              <button
                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                data-testid={`category-${category.id}`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${colorClasses[category.color]} flex items-center justify-center border`}>
                    <category.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base sm:text-lg font-bold text-white">{category.title}</h3>
                    <p className="text-xs sm:text-sm text-slate-400">{category.features.length} features</p>
                  </div>
                </div>
                {expandedCategory === category.id ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>

              {/* Expanded Features */}
              {expandedCategory === category.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="border-t border-white/10"
                >
                  <div className="p-4 sm:p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {category.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div className={`w-8 h-8 rounded-lg ${colorClasses[category.color]} flex items-center justify-center flex-shrink-0 border`}>
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-white">{feature.name}</h4>
                          <p className="text-xs text-slate-400 mt-0.5">{feature.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 grid grid-cols-3 gap-4"
        >
          <div className="text-center p-4 sm:p-6 rounded-xl bg-white/5 border border-white/10">
            <div className="text-2xl sm:text-4xl font-bold text-primary">70+</div>
            <div className="text-xs sm:text-sm text-slate-400 mt-1">Total Features</div>
          </div>
          <div className="text-center p-4 sm:p-6 rounded-xl bg-white/5 border border-white/10">
            <div className="text-2xl sm:text-4xl font-bold text-[#10B981]">9</div>
            <div className="text-xs sm:text-sm text-slate-400 mt-1">Feature Categories</div>
          </div>
          <div className="text-center p-4 sm:p-6 rounded-xl bg-white/5 border border-white/10">
            <div className="text-2xl sm:text-4xl font-bold text-[#6366F1]">1</div>
            <div className="text-xs sm:text-sm text-slate-400 mt-1">Powerful App</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AllFeaturesSection;
